import { MatchingBreakdownCounted, RuleDryRunSkipReason } from '@app/settings/types';

import { MATCHING_SKIP_REASON_BY_WIRE_VALUE } from '../constants';
import { MatchingDataGapEntity, MatchingTier } from '../types';

export interface JustificationSide {
  readonly role: MatchingDataGapEntity;
  readonly name: string;
  readonly tier?: MatchingTier | null;
  readonly date?: string | null;
}

export interface JustificationRow {
  readonly ruleId: string;
  readonly name: string;
  readonly grade: string;
  readonly counted: MatchingBreakdownCounted | null;
  readonly sharePct: number;
  readonly points: number;
}

export interface JustificationSkipped {
  readonly ruleId: string;
  readonly name: string;
  readonly reason: RuleDryRunSkipReason | null;
}

export interface JustificationView {
  // Bench case label (no persisted GC/IP row to build `sides` from, D1); results screen leaves
  // this null and populates `sides` instead.
  readonly label: string | null;
  readonly sides: JustificationSide[];
  readonly score: number | null;
  readonly summary: string;
  readonly pros: string[];
  readonly cons: string[];
  readonly hardFails: string[];
  readonly rows: JustificationRow[];
  readonly skipped: JustificationSkipped[];
}

export interface JustificationInput {
  readonly score?: number | null;
  readonly weightApplied?: number | null;
  readonly summary?: string | null;
  readonly pros?: unknown;
  readonly cons?: unknown;
  readonly hardFails?: unknown;
  readonly breakdown?: unknown;
  readonly skipped?: unknown;
  readonly sides?: JustificationSide[];
  readonly label?: string | null;
}

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const uniqueStrings = (value: unknown): string[] => [
  ...new Set(asArray<unknown>(value).filter((line): line is string => typeof line === 'string')),
];

const COUNTED_VALUES = new Set<string>(Object.values(MatchingBreakdownCounted));

const resolveCounted = (value: unknown): MatchingBreakdownCounted | null =>
  typeof value === 'string' && COUNTED_VALUES.has(value) ? (value as MatchingBreakdownCounted) : null;

const SKIP_REASON_VALUES = new Set<string>(Object.values(RuleDryRunSkipReason));

// Accepts either spelling: the GraphQL enum NAME already (bench path, e.g. `sourceAbsent`) or the
// engine's snake_case wire value (results path, e.g. `source_absent`). Unmapped ⇒ null, never a guess.
const resolveSkipReason = (value: unknown): RuleDryRunSkipReason | null => {
  if (typeof value !== 'string') {
    return null;
  }

  if (SKIP_REASON_VALUES.has(value)) {
    return value as RuleDryRunSkipReason;
  }

  return MATCHING_SKIP_REASON_BY_WIRE_VALUE[value] ?? null;
};

const roundShare = (points: number, weightApplied: number): number =>
  weightApplied > 0 ? Math.round((points / weightApplied) * 100) : 0;

const buildRows = (value: unknown, weightApplied: number): JustificationRow[] =>
  asArray<Record<string, unknown>>(value).reduce<JustificationRow[]>((rows, row) => {
    const ruleId = row?.ruleId;
    const name = row?.name;
    const grade = row?.grade;
    const points = row?.pointsEarned;

    if (
      typeof ruleId !== 'string' ||
      typeof name !== 'string' ||
      typeof grade !== 'string' ||
      typeof points !== 'number'
    ) {
      return rows;
    }

    rows.push({
      ruleId,
      name,
      grade,
      counted: resolveCounted(row?.counted),
      sharePct: roundShare(points, weightApplied),
      points,
    });

    return rows;
  }, []);

// `name` falls back to `ruleId` — same fallback `DryRunBench.tsx` already renders.
const buildSkipped = (value: unknown): JustificationSkipped[] =>
  asArray<Record<string, unknown>>(value).reduce<JustificationSkipped[]>((rows, row) => {
    const ruleId = row?.ruleId;

    if (typeof ruleId !== 'string') {
      return rows;
    }

    const name = row?.name;

    rows.push({ ruleId, name: typeof name === 'string' ? name : ruleId, reason: resolveSkipReason(row?.reason) });

    return rows;
  }, []);

// Shared by the results screen and the dry-run bench — never throws; a malformed/renamed field
// degrades to an empty section.
export const buildJustificationView = (input: JustificationInput | null | undefined): JustificationView => {
  const weightApplied = typeof input?.weightApplied === 'number' ? input.weightApplied : 0;
  const sides = input?.sides;

  return {
    label: typeof input?.label === 'string' ? input.label : null,
    sides: Array.isArray(sides) ? sides : [],
    score: typeof input?.score === 'number' ? input.score : null,
    summary: typeof input?.summary === 'string' ? input.summary : '',
    pros: uniqueStrings(input?.pros),
    cons: uniqueStrings(input?.cons),
    hardFails: uniqueStrings(input?.hardFails),
    rows: buildRows(input?.breakdown, weightApplied),
    skipped: buildSkipped(input?.skipped),
  };
};
